import { DashboardPageFormBody, DashboardPageFormShell } from '@shigo-ui/dashboard-ui';
import { Button, Skeleton, toast } from '@shigo-ui/ui';

import { ReturnBackLink } from '../../../../../components/navigation/ReturnBackLink';
import { useReturnNavigation } from '../../../../../lib/returnNavigation';
import { showRequestError } from '../../../../../lib/requestErrorToast';
import { dashboardT } from '../../../../../lib/messages';
import { usePublisherMutations, usePublisherQuery } from '../../hooks';
import {
  emptyPublisherFormValue,
  publisherToFormValue,
  serializePublisherFormValue,
  type PublisherFormValue,
} from '../../publisherFormSchema';
import { PublisherEditorForm } from './PublisherEditorForm';

export function PublisherEditorPage({ publisherId }: { publisherId?: string }) {
  const { returnTo } = useReturnNavigation({ to: '/supply/publishers' });
  const query = usePublisherQuery(publisherId);
  const mutations = usePublisherMutations();
  const editing = Boolean(publisherId);
  const saving =
    mutations.create.isPending || mutations.update.isPending || mutations.updateCosts.isPending;
  const waitingForFreshDetail = editing && !query.isFetchedAfterMount;
  const goBack = () => void returnTo();

  async function save(value: PublisherFormValue) {
    const payload = serializePublisherFormValue(value);
    let publisherUpdateCompleted = false;
    try {
      if (publisherId && query.data) {
        await mutations.update.mutateAsync({
          id: publisherId,
          payload: { ...query.data, ...payload },
        });
        publisherUpdateCompleted = true;
        await mutations.updateCosts.mutateAsync({
          id: publisherId,
          payload: { allProductsCost: payload.allProductsCost, saasCost: payload.saasCost },
        });
      } else {
        await mutations.create.mutateAsync(payload);
      }
    } catch (error) {
      if (publisherUpdateCompleted) {
        await query.refetch().catch(() => undefined);
      }
      showRequestError(error, dashboardT('publishers.saveError'));
      return;
    }
    toast.success(dashboardT(editing ? 'publishers.updatedSuccess' : 'publishers.createdSuccess'));
    try {
      // Back to where the editor was opened from; the form entry is replaced, not kept.
      await returnTo({ replace: true });
    } catch {
      toast.error(dashboardT('publishers.redirectError'));
    }
  }

  let content = null;
  if (waitingForFreshDetail && !query.isError) {
    content = (
      <div
        role="status"
        aria-label={dashboardT('publishers.loadingDetails')}
        className="grid gap-4 p-6"
      >
        <Skeleton className="h-10 w-72 max-w-full" />
        <Skeleton className="h-80" />
      </div>
    );
  } else if (editing && query.isError) {
    content = (
      <DashboardPageFormBody title={dashboardT('publishers.loadDetailsError')}>
        <Button type="button" variant="outline" onClick={() => void query.refetch()}>
          {dashboardT('publishers.retry')}
        </Button>
      </DashboardPageFormBody>
    );
  } else if (!editing || query.data) {
    content = (
      <PublisherEditorForm
        key={publisherId ?? 'new'}
        initialValue={query.data ? publisherToFormValue(query.data) : emptyPublisherFormValue()}
        editing={editing}
        saving={saving}
        onCancel={goBack}
        onSubmit={save}
      />
    );
  }

  return (
    <DashboardPageFormShell
      title={dashboardT(editing ? 'publishers.editTitle' : 'publishers.createTitle')}
      backAction={
        <ReturnBackLink
          fallback={{ to: '/supply/publishers' }}
          label={dashboardT('publishers.back')}
        />
      }
    >
      {content}
    </DashboardPageFormShell>
  );
}
